import React from 'react';
import { Localizer } from 'services/Localizer';

/**
 * An alert sent to users regarding products or promotions
 */
export interface OrderMessage {
	message: string;
	messageAction: OrderMessageAction;
}

/**
 * The level of action to be taken with an order message
 */
export enum OrderMessageAction {
	/** Display a generic info message */
	Info = 'Info',

	/** Display a generic warning message */
	Warning = 'Warning',

	/** Display a generic error message */
	Error = 'Error',

	/** Display a message and also ask the user to redirect to the cart page */
	RedirectToCart = 'RedirectToCart',

	/** Display an error message related to a failure which occurred during payment processing */
	PaymentError = 'PaymentError',
}

interface OrderMessagesProps {
	orderMessages?: OrderMessage[];

	/** Optional list of the actions which should be displayed. If this is undefined, all actions will be displayed. */
	actions?: OrderMessageAction[];
}

/**
 * Body of the order messages modal which will notify the customer if there were issues with their order including the following states:
 * 1- Redirect to Cart
 * 2- Payment Failure
 * 3- Generic Update Order Errors
 */
function OrderMessages(props: OrderMessagesProps) {
	const { orderMessages, actions } = props;

	// Filter by action if actions were passed in as a prop
	const messages =
		actions == null
			? orderMessages
			: orderMessages?.filter((orderMessage) => actions?.includes(orderMessage.messageAction));

	// Component is omitted if actions filtered out all the messages or there were none to begin with
	if (messages == null || messages.length === 0) {
		return null;
	}

	let headerText = Localizer('GenericOrderMessageHeader');

	// Update header to be more specific if any of the message actions warrant it
	if (messages.some((m) => m.messageAction === OrderMessageAction.RedirectToCart)) {
		// Prioritize RedirectToCart over PaymentError as a RedirectToCart message will also change the modal's button
		// to be 'Review Cart' and send the user to the cart page
		headerText = Localizer('RedirectToCartOrderMessageHeader');
	} else if (messages.some((m) => m.messageAction === OrderMessageAction.PaymentError)) {
		headerText = Localizer('PaymentFailureOrderMessageHeader');
	}

	return (
		<div className="roc-icon-text roc-toggle-form__error">
			<div data-testid="inline-alert-window" className="roc-alert roc-alert-warning roc-alert-full-width">
				<strong className="roc-alert-title roc-alert-title--space">{headerText}</strong>
				<ul>
					{messages.map((orderMessage, index) => (
						<li data-testid="inline-alert-text" key={index}>
							<span dangerouslySetInnerHTML={{ __html: orderMessage.message }} />
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}

export default OrderMessages;
